import React from 'react';

import moment from 'moment/moment';

import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';

import { File as FileInterface } from '@common/typescript/objects/FileInterface';
import Colorbox from '@common/react/components/UI/Colorbox/Colorbox';
import { getUserName } from '@common/react/utils/utils';
import LinkWithPrevLocation from '@common/react/components/UI/LinkWithPrevLocation/LinkWithPrevLocation';
import ReadOnlyControlValue from '@common/react/components/UI/ReadOnlyControlValue/ReadOnlyControlValue';
import Button from '@common/react/components/Forms/Button';
import { FlagSwitcher } from '@common/react/components/UI/PublishSwitcher/PublishSwitcher';

import { dateTimeFormatString } from '@app/components/Utils';
import { AppointmentEmail, AppointmentEmailStatus } from '@app/objects/AppointmentEmail';
import { Links } from '@app/components/UI/Links/Links';

export const extensions = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'webp', 'svg', 'avif'];
export const messageKey = 'send-email-message-key';

interface SendEmailReadonlyProps {
	item: AppointmentEmail;
	patientName?: string;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>,
	setItem: React.Dispatch<React.SetStateAction<AppointmentEmail>>,
	setChanged: React.Dispatch<React.SetStateAction<boolean>>,
}

const SendEmailReadonly: React.FC<SendEmailReadonlyProps> = (props) => {
	const {
		item,
		patientName: propsPatientName,
		setOpen,
		setItem,
		setChanged,
	} = props;

	const [img, setImg] = React.useState<FileInterface | null>(null);
	const sender = item?.from || 'support@northerntuna.com';
	const isInbound = item?.appointmentEmailStatus === AppointmentEmailStatus.Inbound;
	const patientName = item?.patient ? `${item.patient?.firstName} ${item.patient.lastName}`
		: propsPatientName;
	const files = item?.patientFiles || [];
	const photos = React.useMemo(() => {
		return (item?.patientFiles || []).filter((f) => extensions.includes(f.file?.extension?.toLowerCase()));
	}, [files]);
	const imageIndex = React.useMemo(() => {
		const index = photos.findIndex((image) => image.file?.id === img?.file?.id);
		return index < 0 ? null : index;
	}, [photos, img]);

	const tag = (
		<div style={{ verticalAlign: 'middle' }}>
			{item && (
				<Tag
					color="geekblue"
					style={{ margin: '3px 0' }}
				>
					<i className="fa fa-calendar-plus-o" style={{ marginRight: 5 }} />
					{moment(item.time).format(dateTimeFormatString)}
				</Tag>
			)}
		</div>
	);

	const newTab = (
		<div className="pull-right" style={{ height: 0 }}>
			{item && (
				<Tag
					color="geekblue"
					style={{ marginRight: 0 }}
				>
					<LinkWithPrevLocation
						to={{ pathname: '/appointmentEmailView', search: `appointmentEmailId=${item.id}` }}
						target="_blank"
					>
						View at new tab
					</LinkWithPrevLocation>
				</Tag>
			)}
		</div>
	);

	return <>
		<div className="clearfix">
			<div
				style={{
					margin: '-6px 15px 15px 0', lineHeight: '34px', display: 'flex', flexDirection: 'row', gap: 15,
				}}
			>
				<div
					style={{
						fontSize: 14, lineHeight: 1.5, flex: '1 1 auto', width: '1px',
					}}
				>
					<strong>Sender</strong>
					<br />
					<div className="ellipsis">
						<Tooltip title={sender} color="white" placement="topLeft">
							<span>
								{sender}
							</span>
						</Tooltip>
						{isInbound && patientName && (
							<Links.Patient id={item.patient?.id} className="ml10 ellipsis">
								{patientName}
							</Links.Patient>
						)}
						{!isInbound && item.user && (
							<Links.User id={item.user.id} className="ml10 ellipsis" target="_blank">
								{getUserName(item.user)}
							</Links.User>
						)}
					</div>
				</div>
				{tag}
				<div className="appointment-popup email-popup-menu">
					{isInbound && <FlagSwitcher<AppointmentEmail>
						item={item}
						getFlag={(item) => item.read}
						requestType="readEmail"
						getRequestProps={(item) => ({
							id: item.id, read: !item.read,
						})}
						onSuccess={() => {
							setItem({ ...item, read: !item.read });
							setChanged(true);
						}}
						className="appointment-popup__top-icon"
						checkedClassName="text-muted"
						checkedTitle="Set this email as Unread"
						uncheckedTitle="Set this email as Read"
					>
						<i className="fa fa-eye" />
					</FlagSwitcher>}
					<FlagSwitcher<AppointmentEmail>
						item={item}
						getFlag={(item) => item.archive}
						requestType="appointmentEmail"
						getRequestProps={(item) => ({ ...item, archive: !item.archive })}
						onSuccess={() => {
							setItem({ ...item, archive: !item.archive });
							setChanged(true);
						}}
						className="appointment-popup__top-icon"
						checkedClassName="text-muted"
						checkedTitle="Make this email Unarchived"
						uncheckedTitle="Make this email Archived"
					>
						<i className="fa fa-archive" />
					</FlagSwitcher>
				</div>
			</div>
		</div>
		<div
			className="send-email-modal__form custom-scroll send-email-modal__readonly send-email-modal__view"
		>
			<ReadOnlyControlValue
				className="form-group"
				title="To"
				value={<>
					{item.to}
					{!isInbound && item.inquiryId && (
						<Links.Inquiry id={item.inquiryId} className="ml10 ellipsis">
							<Tag color="cyan">Inquiry</Tag>
						</Links.Inquiry>
					)}
					{!isInbound && patientName && (
						<Links.Patient id={item.patient?.id} className="ml10 ellipsis">
							{patientName}
						</Links.Patient>
					)}
				</>}
			/>
			<ReadOnlyControlValue
				className="form-group"
				title="Subject"
				value={item.subject}
			/>
			{newTab}
			<ReadOnlyControlValue
				className={`simple-text ${files.length ? 'form-group' : ''}`}
				title="Content"
				value={<div
					// eslint-disable-next-line
					dangerouslySetInnerHTML={{ __html: item.html || item.text || '-' }}
					className="email-text form-group form-group__bordered"
					style={{ marginBottom: 0 }}
				/>}
			/>
			<Colorbox
				files={photos || []}
				defaultIdx={imageIndex ?? null}
				onCancel={() => setImg(null)}
				zIndex={1002}
			/>
			{!!files.length && (
				<div>
					{files.map((a) => {
						const isImage = extensions.includes(a.file?.extension?.toLowerCase());
						/* eslint-disable */
						return (
							<a
								href={isImage ? undefined : a.file?.src}
								className="attached-file mr10 mb10 clearfix attached-file__readonly"
								key={a.id}
								download={!isImage}
								{...(isImage ? {} : { target: '_blank', rel: 'noreferrer' })}
								onClick={(e) => {
									if (!isImage) return;
									e.preventDefault();
									e.stopPropagation();
									setImg(a);
								}}
							>
								{isImage ? (
									<div
										className="attached-file__image"
									>
										<img
											className="chat-file-tag-image"
											src={a.file?.src}
											alt={a.file?.name}
										/>
									</div>
								) : <i className="fa fa-file" />}
								&nbsp;
								{a.file?.name}
							</a>
						);
						/* eslint-enable */
					})}
				</div>
			)}
		</div>
		<div className="clearfix text-center mt10">
			<Button
				className="btn btn-primary"
				onClick={() => setOpen(false)}
			>
				Close
			</Button>
		</div>
	</>;
};

export default SendEmailReadonly;
